import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { compact } from 'lodash-es';
import { Button, Callout, Card, Column, FullPageModal, FullPageModalHeaderStart, FullPageModalMainActions, Typography, useDevice } from 'seagull';
import { ArrowRight } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { MobileDeviceEnhancer } from '~/components/MobileDeviceEnhancer/MobileDeviceEnhancer';
import { useStore } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { getAccountingDetails } from '~/integrations/Accounting/accounting.utils';
import { selectors } from '~/store/state-selectors';
import { Accounting } from '~/types/api.model';
import styles from './AgreementTriageModal.module.scss';
import { useAgreementTriage } from './hooks/useAgreementTriage';
import { ServicesCard } from './ServicesCard/ServicesCard';
import { TriageAgreementNameRow } from './TriageAgreementNameRow/TriageAgreementNameRow';
import { TriageContactRow } from './TriageContactRow/TriageContactRow';
import { TriageEffectiveDateRow } from './TriageEffectiveDateRow/TriageEffectiveDateRow';
import { TriagePaymentTermsRow } from './TriagePaymentTermsRow/TriagePaymentTermsRow';
import { TriageDivider } from './TriageRow/TriageDivider';
import { TriageTermsOfServiceRow } from './TriageTermsOfServiceRow/TriageTermsOfServiceRow';
export const GetAgreementTriageModalTID = (parentTestId)=>getTestIds(parentTestId);
const AgreementTriageModalContent = (param)=>{
    let { extraction: extractionFromProps, conversationId, dismissModal, testId, contactInactivityCollapseMs } = param;
    const { AgreementTriageModalTestIds } = useMemo(()=>GetAgreementTriageModalTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys();
    const { isMobileDevice } = useDevice();
    const cardClassName = cn(styles.card, {
        [styles.cardMobile]: isMobileDevice
    });
    const isLinkedToQbo = useStore(selectors.integration.isLinkedToQbo);
    const isLinkedToXero = useStore(selectors.integration.isLinkedToXero);
    const [contactValidationRequested, setContactValidationRequested] = useState(false);
    const [isContactFormValid, setIsContactFormValid] = useState(false);
    const contactFormRef = useRef(null);
    const { extraction, setExtraction, isSubmitting, submitCreate } = useAgreementTriage({
        extraction: extractionFromProps,
        conversationId,
        testIds: AgreementTriageModalTestIds
    });
    const showAgreementNameRow = extractionFromProps.agreementName() !== '';
    const showEffectiveDateRow = extractionFromProps.effectiveDate() != null;
    const showContactRow = extractionFromProps.hasContactDetails();
    const fieldsCardRows = compact([
        showAgreementNameRow && {
            key: 'agreementName',
            render: ()=>/*#__PURE__*/ _jsx(TriageAgreementNameRow, {
                    model: extraction,
                    onModelChange: setExtraction
                })
        },
        showContactRow && {
            key: 'contact',
            render: ()=>/*#__PURE__*/ _jsx(TriageContactRow, {
                    model: extraction,
                    onModelChange: setExtraction,
                    onFormValidityChange: setIsContactFormValid,
                    requestValidation: contactValidationRequested,
                    formRef: contactFormRef,
                    inactivityCollapseMs: contactInactivityCollapseMs
                })
        },
        showEffectiveDateRow && {
            key: 'effectiveDate',
            render: ()=>/*#__PURE__*/ _jsx(TriageEffectiveDateRow, {
                    model: extraction,
                    onModelChange: setExtraction
                })
        }
    ]);
    const termsCardRows = compact([
        extraction.netTerms() != null && {
            key: 'paymentTerms',
            render: ()=>/*#__PURE__*/ _jsx(TriagePaymentTermsRow, {
                    model: extraction,
                    onModelChange: setExtraction,
                    wrapperTestId: AgreementTriageModalTestIds.PaymentTermsRow()
                })
        },
        extraction.legalTerms() != null && {
            key: 'termsOfService',
            render: ()=>/*#__PURE__*/ _jsx(TriageTermsOfServiceRow, {
                    model: extraction,
                    onModelChange: setExtraction,
                    wrapperTestId: AgreementTriageModalTestIds.TermsOfServiceRow()
                })
        }
    ]);
    const servicesCopy = t.agreementImport.triageModal.services;
    const servicesSubtitle = (()=>{
        const base = servicesCopy.subtitleBase();
        if (isLinkedToQbo) {
            return "".concat(base, " ").concat(servicesCopy.subtitleMap({
                accountingName: getAccountingDetails(Accounting.QBO).fullName
            }));
        }
        if (isLinkedToXero) {
            return "".concat(base, " ").concat(servicesCopy.subtitleMap({
                accountingName: getAccountingDetails(Accounting.Xero).fullName
            }));
        }
        return base;
    })();
    const fieldsCardClassName = cn(cardClassName, styles.fieldsCard, {
        [styles.fieldsCardMobile]: isMobileDevice
    });
    return /*#__PURE__*/ _jsxs(FullPageModal, {
        testId: AgreementTriageModalTestIds.Root(),
        onClose: dismissModal,
        children: [
            /*#__PURE__*/ _jsx(FullPageModalHeaderStart, {
                children: /*#__PURE__*/ _jsx("span", {
                    children: t.agreementImport.triageModal.title()
                })
            }),
            /*#__PURE__*/ _jsx(FullPageModalMainActions, {
                children: /*#__PURE__*/ _jsx(Button, {
                    testId: AgreementTriageModalTestIds.ContinueButton(),
                    onClick: ()=>{
                        if (showContactRow && extraction.isContactUnmatched() && !isContactFormValid) {
                            var _contactFormRef_current;
                            setContactValidationRequested(true);
                            (_contactFormRef_current = contactFormRef.current) === null || _contactFormRef_current === void 0 ? void 0 : _contactFormRef_current.focusFirstInvalidField();
                            return;
                        }
                        submitCreate();
                    },
                    loading: isSubmitting,
                    disabled: extraction.isLegalTermsUnmatched() && !extraction.legalTermsHasName(),
                    bi: {
                        name: 'agreement_triage_continue'
                    },
                    suffixIcon: /*#__PURE__*/ _jsx(ArrowRight, {}),
                    children: t.agreementImport.triageModal.continueButton()
                })
            }),
            /*#__PURE__*/ _jsxs(Column, {
                className: cn(styles.content, {
                    [styles.contentMobile]: isMobileDevice
                }),
                children: [
                    extraction.hasClientSignature() && /*#__PURE__*/ _jsx(Callout, {
                        testId: AgreementTriageModalTestIds.SignedBanner(),
                        intent: "info",
                        withBorder: true,
                        classes: {
                            wrapper: cardClassName
                        },
                        children: t.agreementImport.triageModal.signedBanner()
                    }),
                    /*#__PURE__*/ _jsx(Card, {
                        bi: {
                            name: 'agreement_triage_fields_card'
                        },
                        borderRadius: "m",
                        className: fieldsCardClassName,
                        testId: AgreementTriageModalTestIds.FieldsCard(),
                        children: fieldsCardRows.map((row, i)=>/*#__PURE__*/ _jsxs(Fragment, {
                                children: [
                                    i > 0 && /*#__PURE__*/ _jsx(TriageDivider, {}),
                                    row.render()
                                ]
                            }, row.key))
                    }),
                    /*#__PURE__*/ _jsx(ServicesCard, {
                        extraction: extraction,
                        setExtraction: setExtraction,
                        className: cardClassName,
                        testId: AgreementTriageModalTestIds.ServicesCard()
                    }),
                    termsCardRows.length > 0 && /*#__PURE__*/ _jsx(Card, {
                        bi: {
                            name: 'agreement_triage_terms_card'
                        },
                        borderRadius: "m",
                        className: fieldsCardClassName,
                        testId: AgreementTriageModalTestIds.TermsCard(),
                        children: termsCardRows.map((row, i)=>/*#__PURE__*/ _jsxs(Fragment, {
                                children: [
                                    i > 0 && /*#__PURE__*/ _jsx(TriageDivider, {}),
                                    row.render()
                                ]
                            }, row.key))
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        skin: "standard",
                        className: cardClassName,
                        testId: AgreementTriageModalTestIds.ServicesSubtitle(),
                        children: servicesSubtitle
                    })
                ]
            })
        ]
    });
};
export const AgreementTriageModal = (props)=>{
    return /*#__PURE__*/ _jsx(MobileDeviceEnhancer, {
        children: /*#__PURE__*/ _jsx(AgreementTriageModalContent, _object_spread({}, props))
    });
};
